/*
 * MDB shim for MDBTableBody
 * Files currently importing this symbol from "mdbreact":
 * - src/Seating/Accounts/accountList.jsx
 * - src/Seating/Patient/PatientViewComponents/CreateQuoteModalFromTicket.jsx
 * - src/Seating/PurchaseQueue/inventoryRequest.jsx
 * - src/Seating/PurchaseQueue/inventoryRequestTable.jsx
 * - src/Seating/PurchaseQueue/purchaseQueueTable.jsx
 * - src/Seating/Settings/creditCardTypes.jsx
 * - src/Seating/Settings/deliveryAcknowledgements.jsx
 * - src/Seating/Settings/editProductSubTypes.jsx
 * - src/Seating/Settings/expenseTypes.jsx
 * - src/Seating/Settings/reasons.jsx
 * - src/Seating/Settings/trainingLink.js
 */

import { useEffect, useState } from "react";
import MUITableBody from "@mui/material/TableBody";
import MUITableRow from "@mui/material/TableRow";
import MUITableCell from "@mui/material/TableCell";
import MUITableFooter from "@mui/material/TableFooter";
import MUITablePagination from "@mui/material/TablePagination";

export default function MDBTableBody({
    children,
    rows = [],
    columns = [],
    ...rest
}) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    const isPaginated = rows.length > 500;
    const maximumPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
    const displayedPage = Math.min(page, maximumPage);

    /**
     * Updates the displayed page.
     *
     * @param {object} event The pagination click event.
     * @param {number} newPage The zero-based page to display.
     * @returns {void}
     */
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    /**
     * Updates the number of rows displayed on each page.
     *
     * @param {object} event The rows-per-page change event.
     * @returns {void}
     */
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    /**
     * Clamps the displayed page to the available rows.
     *
     * @returns {void}
     */
    const clampPageToRows = () => {
        if (page > maximumPage) {
            setPage(maximumPage);
        }
    };

    useEffect(clampPageToRows, [page, rows.length, rowsPerPage]);

    const displayedRows = isPaginated
        ? rows.slice(
            displayedPage * rowsPerPage,
            displayedPage * rowsPerPage + rowsPerPage
        )
        : rows;

    if (children) {
        return <MUITableBody {...rest}>{children}</MUITableBody>;
    }

    const columnCount =
        columns.length > 0
            ? columns.length
            : Math.max(
                ...rows.map((row) =>
                    Object.keys(row).filter(
                        (key) => key !== "clickEvent"
                    ).length
                ),
                1
            );

    return (
        <>
            <MUITableBody {...rest}>
                {displayedRows.map((row, rowIndex) => {
                    const rowKey =
                        row.id ?? `row-${displayedPage}-${rowIndex}`;

                    return (
                        <MUITableRow
                            key={rowKey}
                            onClick={row.clickEvent}
                            hover={Boolean(row.clickEvent)}
                            sx={{
                                cursor: row.clickEvent ? "pointer" : "default",
                            }}
                        >
                            {columns.length === 0
                                ? Object.entries(row)
                                    .filter((entry) => entry[0] !== "clickEvent")
                                    .map((entry, cellIndex) => (
                                        <MUITableCell
                                            key={entry[0] || cellIndex}
                                            align="center"
                                        >
                                            {entry[1]}
                                        </MUITableCell>
                                    ))
                                : columns.map((column, columnIndex) => (
                                    <MUITableCell
                                        key={`${rowKey}-${column.field ?? columnIndex}`}
                                        align={column.align ?? "center"}
                                    >
                                        {row[column.field]}
                                    </MUITableCell>
                                ))
                            }
                        </MUITableRow>
                    );
                })}
            </MUITableBody>

            {isPaginated && (
                <MUITableFooter>
                    <MUITableRow>
                        <MUITablePagination
                            colSpan={columnCount}
                            count={rows.length}
                            page={displayedPage}
                            onPageChange={
                                handleChangePage
                            }
                            rowsPerPage={
                                rowsPerPage
                            }
                            onRowsPerPageChange={
                                handleChangeRowsPerPage
                            }
                            rowsPerPageOptions={[
                                25,
                                50,
                                100,
                            ]}
                            /** I would avoid changing classes in here, but since this is a shim already... */
                            sx={{
                                verticalAlign: "middle",
                                borderBottom: 0,
                                "& .MuiTablePagination-toolbar": {
                                    minHeight: 52,
                                    display: "flex",
                                    alignItems: "center",
                                },
                                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                                    margin: 0,
                                    display: "flex",
                                    alignItems: "center",
                                },
                                "& .MuiInputBase-root": {
                                    display: "flex",
                                    alignItems: "center",
                                },
                                "& .MuiTablePagination-select": {
                                    display: "flex",
                                    alignItems: "center",
                                },
                                "& p": {
                                    margin: 0,
                                },
                            }}
                        />
                    </MUITableRow>
                </MUITableFooter>
            )}
        </>
    );
}
